import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import Icon from '../AppIcon';
import Button from './Button';
import OrgSwitcher from "./OrgSwitcher";

const Header = ({ onSidebarToggle, sidebarExpanded }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const { currentOrganization } = useOrganization();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const userMenuRef = useRef(null);
  const location = useLocation();



  const handleUserMenuClick = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.log('Logout error:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  return (
    <header className={`fixed top-0 right-0 h-16 bg-sidebar border-b border-border z-200 shadow-sm transition-all duration-300 ease-in-out left-0 ${sidebarExpanded ? 'lg:left-64' : 'lg:left-16'}`}>
      <div className="flex items-center justify-between h-full px-4">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={onSidebarToggle}
            className="p-2 hover:bg-accent transition-micro lg:hidden"
            iconName="Menu"
            iconSize={20}
          />
          
          {/* Organization Logo/Name - Mobile & Desktop */}
          {currentOrganization && (
            <div className="flex items-center">
              {currentOrganization.logo_url ? (
                <img 
                  src={currentOrganization.logo_url} 
                  alt={currentOrganization.business_name || currentOrganization.name}
                  className="h-8 w-auto max-w-32 object-contain"
                  onError={(e) => {
                    // Fallback to business name if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <span 
                className={`font-medium text-sidebar-foreground ${currentOrganization.logo_url ? 'hidden' : 'block'}`}
                style={{ display: currentOrganization.logo_url ? 'none' : 'block' }}
              >
                {currentOrganization.business_name || currentOrganization.name}
              </span>
            </div>
          )}
        </div>

        {/* Center Section - Spacer */}
        <div className="flex-1"></div>

        {/* Right Section */}
        <div className="flex items-center space-x-2">
          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <Button
              variant="ghost"
              onClick={handleUserMenuClick}
              className="p-2 hover:bg-surface-hover transition-micro flex items-center space-x-2"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <Icon name="User" size={16} className="text-primary" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-text-primary">
                  {userProfile?.full_name || user?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-xs text-text-secondary capitalize">
                  {userProfile?.role || 'User'}
                </div>
              </div>
              <Icon name="ChevronDown" size={16} className="text-text-muted" />
            </Button>

            {/* User Menu Dropdown */}
            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 w-56 bg-surface border border-border rounded-lg shadow-lg z-300">
                <div className="p-4 border-b border-border">
                  <div className="font-medium text-text-primary">
                    {userProfile?.full_name || user?.email?.split('@')[0] || 'User'}
                  </div>
                  <div className="text-sm text-text-secondary">{user?.email}</div>
                  <div className="text-xs text-text-muted mt-1 capitalize">
                    {userProfile?.role || 'User'}
                  </div>
                </div>
                <div className="py-2">
                  <button 
                    onClick={() => {
                      navigate('/settings?tab=account');
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-hover transition-micro flex items-center space-x-2"
                  >
                    <Icon name="User" size={16} />
                    <span>Profile Settings</span>
                  </button>
                  <button 
                    onClick={() => {
                      navigate('/settings');
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-hover transition-micro flex items-center space-x-2"
                  >
                    <Icon name="Settings" size={16} />
                    <span>Settings</span>
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-hover transition-micro flex items-center space-x-2">
                    <Icon name="HelpCircle" size={16} />
                    <span>Help & Support</span>
                  </button>
                </div>
                <div className="border-t border-border py-2">
                  <button 
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error-50 transition-micro flex items-center space-x-2"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;